/**
 * Pre-hydration gate for the auth pages.
 *
 * `/auth/login` is server-rendered, so its form is on screen and clickable the
 * moment the HTML lands. The "you are already signed in, go inside" decision,
 * however, is client-side and used to wait for `isInitialized` — which is set
 * only after `AuthInitializer` validates the stored token over the network.
 * Between those two moments (bundle + hydration + one round trip) a signed-in
 * visitor arriving from a landing CTA stared at a live login form long enough
 * to start typing a second sign-in. This script closes that window from the
 * first paint: the session snapshot it needs (a token in localStorage) is
 * readable synchronously, so no request has to finish first.
 *
 * It hides the page instead of navigating: navigation stays with `AuthLayout`,
 * which resolves the real destination (?redirect= → building) as a soft App
 * Router transition. A `location.replace` here would cost a second full
 * document load and would race that resolution.
 *
 * Paddle's domain-approval bot does not execute JavaScript, so it never runs
 * this gate and keeps seeing the full server-rendered login page with its
 * legal/pricing anchors (see `LegalFooterLinks`). Nothing here may move to the
 * server: a server-side auth branch would make the prerendered HTML depend on
 * a visitor identity the server does not know, which is exactly the "too
 * dynamic" shape that failed domain approval before.
 *
 * The attribute is removed again by `AuthLayout` as soon as the store settles
 * on "not signed in after all" (a stale or revoked token), so the form appears
 * without a reload.
 */

// Auth pages that bounce an already-signed-in visitor. Deliberately NOT the
// whole /auth/* space: reset-password stays reachable while signed in, and the
// OAuth callbacks must run their own exchange (see BYPASS in app/auth/layout).
export const GATED_AUTH_PATHS = [
  '/auth/login',
  '/auth/register',
  '/auth/forgot-password',
];

export const AUTH_GATE_ATTRIBUTE = 'data-auth-gate';

export function getAuthGateScript(): string {
  const gatedPaths = JSON.stringify(GATED_AUTH_PATHS);

  return `(function(){try{
var p=window.location.pathname.replace(/\\/+$/,'')||'/';
if(${gatedPaths}.indexOf(p)===-1)return;
if(window.location.search.indexOf('email=')!==-1)return;
var t=null;try{t=window.localStorage.getItem('auth_token')}catch(e){}
if(!t)return;
var d=document.documentElement;
d.setAttribute('${AUTH_GATE_ATTRIBUTE}','pending');
var s=document.createElement('style');
s.textContent='html[${AUTH_GATE_ATTRIBUTE}=pending] body{visibility:hidden}'+'html[${AUTH_GATE_ATTRIBUTE}=pending]::after{content:"";position:fixed;inset:0;background:oklch(1 0 0);z-index:2147483646}'+'html.dark[${AUTH_GATE_ATTRIBUTE}=pending]::after{background:oklch(0.29 0 0)}'+'html[${AUTH_GATE_ATTRIBUTE}=pending]::before{content:"";position:fixed;top:50%;left:50%;width:2rem;height:2rem;margin:-1rem 0 0 -1rem;border-radius:9999px;border:2px solid transparent;border-bottom-color:oklch(0.58 0.1 188.6);animation:auth-gate-spin 1s linear infinite;z-index:2147483647}'+'html.dark[${AUTH_GATE_ATTRIBUTE}=pending]::before{border-bottom-color:oklch(0.73 0.126 188.9)}'+'@keyframes auth-gate-spin{to{transform:rotate(360deg)}}';
document.head.appendChild(s);
}catch(e){}})();`;
}
